import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  Box,
} from "@mui/material";

const LeftSideNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Define all menu items
  const allMenuItems = [
    { label: "Home", path: "/" },
    {
      section: "Programme",
      items: [
        {
          label: "Bio Diversity Documentation",
          path: "/biodiversity-of-lakshadweep",
        },
        { label: "Coral Reef Restoration", path: "/restoring-hope" },
        {
          label: "Community and Resource Governance",
          path: "/community-resource",
        },
        {
          label: "Community wellbeing and Environmental",
          path: "/community-welbeing",
        },
        { label: "Sustainable Fisheries", path: "/sustainable-fisheries" },
      ],
    },
    {
      section: "Publication",
      items: [
        { label: "Reports", path: "/report" },
        {
          label: "Scientific Papers",
          subitems: [
            { label: "Journal Article", path: "/journal-article" },
            { label: "Editorial", path: "/editorial" },
            { label: "Popular Article", path: "/popular-article" },
            {
              label: "Opinion and Commentaries",
              path: "/opinionand-commentaries",
            },
            {
              label: "Book (Children's Magazine and Newsletter)",
              path: "/book",
            },
          ],
        },
        {
          label: "Outreach Materials",
          subitems: [
            { label: "Education Outreach", path: "/education-outreach" },
            { label: "Field Guide", path: "/field-guide" },
            {
              label: "Modules and Guide Book for Practitioners",
              path: "/modules-and-guide",
            },
            { label: "Future Calendar Series", path: "/future-calendar" },
            { label: "Documentary", path: "/documentary" },
          ],
        },
      ],
    },
    {
      section: "People",
      items: [
        { label: "Founder Member", path: "/founder" },
        { label: "Administrative Member", path: "/administrative" },
        { label: "Project Member", path: "/project-members" },
        // { label: "Advisiors Member", path: "" },
        { label: "Advisory Board Members", path: "/advisory-board-members" },
      ],
    },
  ];

  // Determine which section to show based on current path
  const getFilteredMenuItems = () => {
    // First check if we're in a Programme sub-item
    const programmePaths =
      allMenuItems
        .find((item) => item.section === "Programme")
        ?.items.map((i) => i.path) || [];
    if (programmePaths.includes(location.pathname)) {
      return [
        allMenuItems[0], // Home
        allMenuItems[1], // Programme
      ];
    }

    // Then check if we're in a Publication sub-item
    const publicationItems =
      allMenuItems.find((item) => item.section === "Publication")?.items || [];
    const publicationPaths = publicationItems.flatMap((item) =>
      item.subitems ? item.subitems.map((sub) => sub.path) : [item.path]
    );
    if (publicationPaths.includes(location.pathname)) {
      return [
        allMenuItems[0], // Home
        allMenuItems[2], // Publication
      ];
    }

    // Then check if we're in a People sub-item
    const peoplePaths =
      allMenuItems
        .find((item) => item.section === "People")
        ?.items.map((i) => i.path) || [];
    if (peoplePaths.includes(location.pathname)) {
      return [
        allMenuItems[0], // Home
        allMenuItems[3], // People
      ];
    }

    // Default case - show all items
    return allMenuItems;
  };

  const menuItems = getFilteredMenuItems();

  const handleItemClick = (path, external = false) => {
    if (external) {
      window.location.href = path;
    } else {
      navigate(path);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { md: 300, xs: 400 },
        background: "#f0fbfc",
        overflowY: "auto",
        boxShadow: "3px 0 5px rgba(0,0,0,0.08)",
        // border: '1px solid #d0e5e8',
      }}
    >
      <List component="nav" sx={{ p: 0 }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            {item.section ? (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    px: 2,
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    fontFamily: "Montserrat, sans-serif",
                    color: "#333",
                  }}
                >
                  {item.section}
                </Typography>
                {item.items.map((subItem, subIndex) => (
                  <React.Fragment key={`${index}-${subIndex}`}>
                    <ListItem
                      button
                      onClick={() =>
                        !subItem.subitems &&
                        handleItemClick(subItem.path, subItem.external)
                      }
                      sx={{
                        pl: 3,
                        py: 0.9,
                        fontFamily: "Montserrat, sans-serif",
                        backgroundColor: isActive(subItem.path)
                          ? "#c3e6ea"
                          : "transparent",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#e1f5f7",
                        },
                      }}
                    >
                      <ListItemText
                        sx={{ cursor: "pointer" }}
                        primary={subItem.label}
                        primaryTypographyProps={{
                          fontSize: "0.85rem",
                          color: "#333",
                          fontFamily: "Montserrat, sans-serif",
                          cursor: "pointer",
                        }}
                      />
                    </ListItem>
                    {subItem.subitems &&
                      subItem.subitems.map((subSubItem, subSubIndex) => (
                        <ListItem
                          key={`${index}-${subIndex}-${subSubIndex}`}
                          button
                          onClick={() => handleItemClick(subSubItem.path)}
                          sx={{
                            pl: 5,
                            py: 0.8,
                            backgroundColor: isActive(subSubItem.path)
                              ? "#c3e6ea"
                              : "transparent",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "#e1f5f7",
                            },
                          }}
                        >
                          <ListItemText
                            primary={subSubItem.label}
                            primaryTypographyProps={{
                              fontSize: "0.8rem",
                              color: "#444",
                              fontFamily: "Montserrat, sans-serif",
                            }}
                          />
                        </ListItem>
                      ))}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <ListItem
                button
                onClick={() => handleItemClick(item.path, item.external)}
                sx={{
                  pl: 2,
                  py: 1,
                  cursor: "pointer",
                  backgroundColor: isActive(item.path)
                    ? "#c3e6ea"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: "#e1f5f7",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    color: "#333",
                    fontFamily: "Montserrat, sans-serif",
                    cursor: "pointer",
                  }}
                />
              </ListItem>
            )}
            {index < menuItems.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default LeftSideNavigation;
